import React, { useMemo, useState, useEffect } from "react"
import { useKBar } from "kbar"
import algoliasearch from "algoliasearch/lite"
import { InstantSearch, Index, Configure, connectStateResults } from "react-instantsearch-dom"
import { AlgoliaHitsToActions } from "./AlgoliaHitsToActions"
import { useCurrentRepo } from "../hooks/useCurrentRepo"
import {
  MIN_SEARCH_LENGTH,
  REPO_CONFIG,
  CROSS_REPO_HITS_PER_PAGE,
  SCOPED_SEARCH_HITS_PER_PAGE,
} from "../constants"

// Track if Algolia search is in progress
const searchingState = { isSearching: false }

const SearchStateTracker = connectStateResults(({ searching }) => {
  searchingState.isSearching = searching
  return null
})

export function AlgoliaSearchIntegration() {
  const { searchQuery } = useKBar((state) => ({
    searchQuery: state.searchQuery,
  }))
  const currentIndex = useCurrentRepo()

  const searchClient = useMemo(
    () =>
      algoliasearch(
        process.env.GATSBY_ALGOLIA_APP_ID,
        process.env.GATSBY_ALGOLIA_SEARCH_KEY
      ),
    []
  )

  if (!searchQuery || searchQuery.length < MIN_SEARCH_LENGTH) {
    searchingState.isSearching = false
    return null
  }

  // Cross-repo search when outside docs (currentIndex is null)
  if (!currentIndex) {
    const allIndices = Object.keys(REPO_CONFIG)

    return (
      <div style={{ position: "absolute", left: -9999 }}>
        <InstantSearch searchClient={searchClient} indexName={allIndices[0]}>
          <Configure query={searchQuery} hitsPerPage={CROSS_REPO_HITS_PER_PAGE} />
          <SearchStateTracker />
          {allIndices.map((indexName) => (
            <Index key={indexName} indexName={indexName}>
              <AlgoliaHitsToActions indexName={indexName} />
            </Index>
          ))}
        </InstantSearch>
      </div>
    )
  }

  // Scoped search when inside docs
  return (
    <div style={{ position: "absolute", left: -9999 }}>
      <InstantSearch searchClient={searchClient} indexName={currentIndex}>
        <Configure query={searchQuery} hitsPerPage={SCOPED_SEARCH_HITS_PER_PAGE} />
        <SearchStateTracker />
        <Index indexName={currentIndex}>
          <AlgoliaHitsToActions />
        </Index>
      </InstantSearch>
    </div>
  )
}

export function useAlgoliaSearching() {
  const [isSearching, setIsSearching] = useState(false)
  const { searchQuery } = useKBar((state) => ({
    searchQuery: state.searchQuery,
  }))

  useEffect(() => {
    const interval = setInterval(() => {
      setIsSearching(searchingState.isSearching)
    }, 50)
    return () => clearInterval(interval)
  }, [searchQuery])

  return isSearching
}
